/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */

/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	useExperienceRenderAndMountMark,
	ExperienceMark,
} from '../../common/utils/experience-tracker';
import messages from './messages';

/*
  In the Remote App Switcher, the Skeleton component shows up first as a fallback while the full Remote App Switcher is still loading.
  Since the styles (compiled CSS) are lazy-loaded with the full App Switcher, they are not be ready when the Skeleton appears.
  This causes visual issues where the Skeleton looks broken

  To avoid this, we use inline styles instead. This guarantees that all required styles are applied immediately with the component.
  https://hello.jira.atlassian.cloud/browse/NAVX-676
*/
const iconSkeleton = (
	<Box
		style={{
			display: 'inline-block',
			width: 32,
			height: 32,
			backgroundColor: token('color.skeleton'),
			borderRadius: token('radius.large'),
			marginRight: token('space.100'),
		}}
	/>
);
const lineSkeleton = (
	<Box
		style={{
			display: 'inline-block',
			width: 260,
			height: 10,
			backgroundColor: token('color.skeleton'),
			borderRadius: token('radius.small'),
		}}
	/>
);

export default (): JSX.Element => {
	useExperienceRenderAndMountMark(ExperienceMark.SWITCHER_SKELETON_MOUNT);
	const { formatMessage } = useIntl();

	const items = (
		<Box
			aria-hidden="true"
			style={{
				display: 'flex',
				paddingInline: token('space.200'),
				paddingBlock: token('space.100'),
				alignItems: 'center',
			}}
		>
			{iconSkeleton}
			{lineSkeleton}
		</Box>
	);

	return (
		<Box
			as="section"
			style={{ width: '343px', height: '80vh' }}
			role="alert"
			aria-label={formatMessage(messages.skeletonLoaderAriaLabel)}
			testId="skeleton"
		>
			<Box
				aria-hidden="true"
				style={{
					paddingInline: token('space.200'),
					paddingTop: token('space.300'),
					paddingBottom: token('space.100'),
				}}
			>
				{lineSkeleton}
			</Box>
			{items}
			{items}
			{items}
		</Box>
	);
};
